import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Flex, Spin } from 'antd'
import {
  SYSTEM_TYPE,
  SystemType,
  useSystemStore,
} from '@/app/store/systemStore'
import { routes } from '@/shared/config/routes'
import {
  SystemContainer,
  SystemCard,
  SystemLogo,
  SystemGrid,
  SystemItem,
  SystemTitle,
  LogoutBtn,
  SystemHeader,
} from './styled'
import { Icon } from '@/shared/ui/Icon/Icon'
import { useAuthStore } from '@/app/store/authStore'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import { SystemAuthForm } from '../components/SystemAuthForm'
import { useSystemAuthCheck, VerifyResult } from '../model/useSystemAuthCheck'
import { useProfileQuery } from '../model/useProfileQuery'
import { GLONASS_SOFT_ADMINS } from '@/shared/config/admins'
import { RetryOnError } from '@/shared/ui/components/RetryOnError'

const { Title } = Typography

const PERMISSIONS = {
  OMNICOMM: 'transfer.omnicomm',
  AXENTA: 'transfer.axenta',
  GELIOS: 'transfer.gelios',
  FORTMONITOR: 'transfer.fortmonitor',
  GLONASSSOFT: 'transfer.glonasssoft',
} as const

export const SystemPage: React.FC = () => {
  const navigate = useNavigate()
  const { profile, logout, user, token, currentServer } = useAuthStore()
  const { setSelectedSystem } = useSystemStore()
  const { verifyAndSaveCredentials, saveCredentials } = useSystemAuthCheck()
  const { isSm, isXs, isMd } = useBreakpoint()
  const isMobile = isXs || isSm
  const isTablet = isXs || isSm || isMd
  const [selectedSystemForAuth, setSelectedSystemForAuth] =
    useState<SystemType | null>(null)

  const {
    data: freshProfile,
    isPending: isProfileLoading,
    isError: isProfileError,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileQuery({
    token,
    currentServer,
  })
  const actualProfile = freshProfile ?? profile

  const permissionsSet = useMemo(() => {
    return new Set(actualProfile?.permissions || [])
  }, [actualProfile])

  const isOmnicommAdmin = permissionsSet.has(PERMISSIONS.OMNICOMM)
  const isAxentaAdmin = permissionsSet.has(PERMISSIONS.AXENTA)
  const isGeliosAdmin = permissionsSet.has(PERMISSIONS.GELIOS)
  const isFortMonitorAdmin = permissionsSet.has(PERMISSIONS.FORTMONITOR)
  const isGlonassoftAdmin =
    permissionsSet.has(PERMISSIONS.GLONASSSOFT) ||
    (user?.username !== undefined && GLONASS_SOFT_ADMINS.has(user?.username))

  const handleSystemSelect = (system: SystemType) => {
    switch (system) {
      case SYSTEM_TYPE.OMNICOMM:
        if (isOmnicommAdmin) {
          setSelectedSystemForAuth(SYSTEM_TYPE.OMNICOMM)
        }
        break

      case SYSTEM_TYPE.AXENTA:
        if (isAxentaAdmin) {
          setSelectedSystemForAuth(SYSTEM_TYPE.AXENTA)
        }
        break

      case SYSTEM_TYPE.GELIOS:
        setSelectedSystemForAuth(SYSTEM_TYPE.GELIOS)
        break

      case SYSTEM_TYPE.FORTMONITOR:
        setSelectedSystemForAuth(SYSTEM_TYPE.FORTMONITOR)
        break

      case SYSTEM_TYPE.WIALON:
        setSelectedSystem(SYSTEM_TYPE.WIALON)
        navigate(routes.transfer)
        break

      case SYSTEM_TYPE.GLONASSOFT:
        setSelectedSystemForAuth(SYSTEM_TYPE.GLONASSOFT)
        break

      default:
        console.warn(system)
        break
    }
  }

  const handleAuthSuccess = async (credentials: {
    login: string
    password: string
  }): Promise<VerifyResult> => {
    if (!selectedSystemForAuth) {
      return { success: false, error: 'Система не выбрана' }
    }

    const systemType = selectedSystemForAuth as
      | SYSTEM_TYPE.OMNICOMM
      | SYSTEM_TYPE.AXENTA
      | SYSTEM_TYPE.GELIOS
      | SYSTEM_TYPE.FORTMONITOR
      | SYSTEM_TYPE.GLONASSOFT

    if (systemType === SYSTEM_TYPE.GELIOS) {
      saveCredentials(systemType, credentials)
      setSelectedSystem(SYSTEM_TYPE.GELIOS)
      navigate(routes.transfer)
      return { success: true }
    }

    const result = await verifyAndSaveCredentials(systemType, credentials)

    if (result.success) {
      setSelectedSystem(systemType)
      navigate(routes.transfer)
    }

    return result
  }

  const handleBackToSystemSelect = () => {
    setSelectedSystemForAuth(null)
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate(routes.login)
    } catch (error) {
      console.error('❌ Logout error:', error)
    }
  }

  const systems = [
    {
      id: SYSTEM_TYPE.WIALON,
      name: 'Wialon',
      logo: '/images/wialon_logo.svg',
      visible: true,
    },
    {
      id: SYSTEM_TYPE.OMNICOMM,
      name: 'Omnicomm',
      logo: '/images/omnicomm_logo.svg',
      visible: isOmnicommAdmin,
    },
    {
      id: SYSTEM_TYPE.AXENTA,
      name: 'Axenta',
      logo: isTablet
        ? '/images/axenta_auth_logo.svg'
        : '/images/axenta_logo.svg',
      visible: isAxentaAdmin,
    },
    {
      id: SYSTEM_TYPE.GELIOS,
      name: 'Gelios',
      logo: '/images/gelios_logo.svg',
      visible: isGeliosAdmin,
    },
    {
      id: SYSTEM_TYPE.FORTMONITOR,
      name: 'FortMonitor',
      logo: '/images/fortmonitor_logo.svg',
      visible: isFortMonitorAdmin,
    },
    {
      id: SYSTEM_TYPE.GLONASSOFT,
      name: 'ГЛОНАССSoft',
      logo: '/images/glonassoft_logo.svg',
      visible: isGlonassoftAdmin,
    },
  ].filter((system) => system.visible)

  if (
    selectedSystemForAuth === SYSTEM_TYPE.OMNICOMM ||
    selectedSystemForAuth === SYSTEM_TYPE.AXENTA ||
    selectedSystemForAuth === SYSTEM_TYPE.GELIOS ||
    selectedSystemForAuth === SYSTEM_TYPE.FORTMONITOR ||
    selectedSystemForAuth === SYSTEM_TYPE.GLONASSOFT
  ) {
    const systemType = selectedSystemForAuth as
      | SYSTEM_TYPE.OMNICOMM
      | SYSTEM_TYPE.AXENTA
      | SYSTEM_TYPE.GELIOS
      | SYSTEM_TYPE.FORTMONITOR
      | SYSTEM_TYPE.GLONASSOFT

    return (
      <SystemContainer>
        <SystemAuthForm
          systemType={systemType}
          onAuthSuccess={handleAuthSuccess}
          onBack={handleBackToSystemSelect}
        />
      </SystemContainer>
    )
  }

  return (
    <SystemContainer>
      <SystemCard>
        <Flex vertical gap={isMobile ? 32 : 24}>
          <SystemHeader vertical gap={32} align="center">
            {!isTablet && (
              <SystemLogo src="/images/logo.svg" alt="ГлонассSoft Transfer" />
            )}
            <Flex vertical gap={8} align="center">
              <Title
                level={4}
                style={{
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                Выберите систему для экспорта
              </Title>
            </Flex>
          </SystemHeader>

          <Spin spinning={isProfileLoading}>
            <RetryOnError
              isError={isProfileError && !freshProfile}
              label="Не удалось загрузить профиль"
              isRetrying={isProfileFetching}
              onRetry={() => refetchProfile()}
            >
              <SystemGrid>
                {systems.map((system) => (
                  <SystemItem
                    align="center"
                    justify={isTablet ? 'space-between' : 'center'}
                    key={system.id}
                    onClick={() => handleSystemSelect(system.id)}
                  >
                    <Flex>
                      <img src={system.logo} alt={system.name} />
                    </Flex>
                    <SystemTitle>{system.name}</SystemTitle>
                  </SystemItem>
                ))}
              </SystemGrid>
            </RetryOnError>
          </Spin>
        </Flex>
        <LogoutBtn
          size="small"
          style={{ width: '32px' }}
          onClick={handleLogout}
          icon={<Icon name="user_logout" size={16} />}
        />
      </SystemCard>
    </SystemContainer>
  )
}
